import { useCallback, useRef, useState } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useReleaseRefundHoldMutation } from '../../store/api';
import { serverReasonOf } from '../balance-table/serverReason';

import {
  type HeldCharge,
  type HeldChargeRefusal,
  releaseOutcomeText,
  releaseRequestOf,
  UNKNOWN_OUTCOME_TEXT,
} from './heldCharges';

export type HeldChargeSurface = 'encounter_payments' | 'patient_balance_table';

interface Options {
  patientId: string;
  surface: HeldChargeSurface;
  /** Also report a refusal where the surface has no line to show it on. */
  onRefused?: (message: string) => void;
}

export interface BillHeldCharge {
  billPatient: (charge: HeldCharge) => Promise<void>;
  /** The charge whose Bill patient is in flight. */
  pendingChargeLineId: string | null;
  /** The server's refusals, by charge line, in its own words. */
  refusals: Record<string, HeldChargeRefusal>;
  clearRefusals: () => void;
}

const without = (
  refusals: Record<string, HeldChargeRefusal>,
  chargeLineId: string,
): Record<string, HeldChargeRefusal> =>
  Object.fromEntries(Object.entries(refusals).filter(([id]) => id !== chargeLineId));

/**
 * Bill patient on a charge held after a refund (gdn-4242). The server first
 * applies the patient's own unapplied payments at the visit, so the answer
 * can be "paid" as well as "billed"; the snackbar says which. The release's
 * cache tags refresh every surface that shows the charge.
 */
export function useBillHeldCharge({ patientId, surface, onRefused }: Options): BillHeldCharge {
  const [release] = useReleaseRefundHoldMutation();
  const { enqueueSnackbar } = useSnackbar();
  const { singular } = useSubjectLanguageVariants();
  const { billing } = useEventTracking();
  const [pendingChargeLineId, setPendingChargeLineId] = useState<string | null>(null);
  const [refusals, setRefusals] = useState<Record<string, HeldChargeRefusal>>({});
  // A second click before the first answer lands would only earn a 409.
  const inFlight = useRef(new Set<string>());

  const billPatient = useCallback(
    async (charge: HeldCharge) => {
      if (inFlight.current.has(charge.chargeLineId)) return;
      inFlight.current.add(charge.chargeLineId);
      billing.patientHeldChargeBillSubmitted({
        patient_id: patientId,
        charge_line_id: charge.chargeLineId,
        amount_cents: charge.amountCents,
        surface,
      });
      setPendingChargeLineId(charge.chargeLineId);
      setRefusals(current => without(current, charge.chargeLineId));
      try {
        const { charge_lines: released } = await release({
          patientId,
          releaseRefundHoldRequest: releaseRequestOf(charge),
        }).unwrap();
        const line = released.find(item => item.charge_line_id === charge.chargeLineId);
        enqueueSnackbar(
          line
            ? releaseOutcomeText(line.patient_due_status, singular)
            : `Billed to the ${singular}.`,
          { variant: 'success' },
        );
      } catch (error) {
        const message = serverReasonOf(error) ?? UNKNOWN_OUTCOME_TEXT;
        setRefusals(current => ({
          ...current,
          [charge.chargeLineId]: { label: charge.label, message },
        }));
        onRefused?.(message);
      } finally {
        inFlight.current.delete(charge.chargeLineId);
        setPendingChargeLineId(null);
      }
    },
    [billing, enqueueSnackbar, onRefused, patientId, release, singular, surface],
  );

  const clearRefusals = useCallback(() => setRefusals({}), []);

  return { billPatient, pendingChargeLineId, refusals, clearRefusals };
}
